import numeral from "numeral"

const priceFormatByAccuracy = {
  // Default accuracy
  normal: "$0.0a", // TODO: Remove or re-evaluate normal, which is not used apart from in ProjectsTable (deprecated)
  // Accuracy typically when dealing with eg. latest price
  increased: "$0.00a",
  // Not in use, but in the scale. Increased is formatted to this when dealing with fractional, but not build into logic.
  accurate: "$0.0000",
  // Very accurate price used with eg. Stablecoins price
  veryAccurate: "$0.000000",
} as const

const priceFormatToSmallestFloat = (format: string) => {
  const float = Number(format.replace(/\$|a/g, "").replace(/0$/, "1"))
  if (Number.isNaN(float)) {
    // If regex were to fail. Could happen if someone adds weird format and doesn't adjust this
    throw `Could not calculate smallest usable numbery by accuracy, please re-check format (${format}) against the regex`
  }
  return float
}

export type PriceAccuracy = keyof typeof priceFormatByAccuracy

export type PriceOptions<T extends string | undefined = string> = {
  fallback?: T
  noUnit?: boolean
}

const defaultPriceOptions: PriceOptions<string> = {
  fallback: "-",
  noUnit: false,
}

export type LatestPriceLabel = {
  formattedPrice: string
  accuratePrice: string | null
}

export const getLatestPriceLabel = (
  price?: null | number
): LatestPriceLabel => {
  // TODO: migrate to common function?

  if (typeof price !== "number") {
    const formattedPrice = numeral(price).format("$0,0.00[00]")
    return {
      formattedPrice,
      accuratePrice: null,
    }
  }

  if (price === 0) {
    return {
      formattedPrice: "$0.00",
      accuratePrice: null,
    }
  }

  const accuratePriceWithFormatting = numeral(price).format(
    "$0,0.00[0000000000000000000000]"
  )

  if (price > 0 && price < 0.0001) {
    return {
      formattedPrice: "< $0.0001",
      accuratePrice: accuratePriceWithFormatting,
    }
  }

  const formatString = price >= 1.0 ? "$0,0.00" : "$0,0.0000"
  const formattedPrice = numeral(price).format(formatString)

  const isRounded = price !== Number(numeral(formattedPrice).value())

  return {
    formattedPrice: isRounded ? `~${formattedPrice}` : formattedPrice,
    accuratePrice: isRounded ? accuratePriceWithFormatting : null,
  }
}

// TODO: maybe rename to formatPrice and remove old formatPrice
// TODO: remove priceOptions and create separate functions (depending on how things look after the todo above this)
export const getLabelForPrice = <T extends string | undefined = string>(
  value?: number | null,
  accuracy: PriceAccuracy = "increased",
  priceOptions?: PriceOptions<T>
): T extends string ? string : string | undefined => {
  const options = priceOptions ?? defaultPriceOptions // Due to types
  if (typeof value !== "number") {
    return options.fallback as T extends string ? string : string | undefined
  }

  try {
    if (!value) {
      throw "Zero value"
    }
    const absVal = Math.abs(value)
    let format: string = priceFormatByAccuracy[accuracy]

    const smallestUsableNumberByAccuracy = priceFormatToSmallestFloat(format)

    // We, apparently, want to opt out of displaying too small numbers?
    if (accuracy !== "normal" && absVal < smallestUsableNumberByAccuracy) {
      throw "Rounded to zero"
    }

    const isFractionalValue = absVal < 1
    if (
      accuracy !== "normal" &&
      isFractionalValue &&
      accuracy !== "accurate" &&
      accuracy !== "veryAccurate"
    ) {
      // If format not already most accurate we use and not a normal one either, display fractional values with increased accuracy
      format = `${format.replace("a", "")}00`
    }

    const formattedValue = numeral(value).format(format)

    if (formattedValue === "$NaN" || formattedValue === "N$aN") {
      /*
       * @url https://github.com/adamwdraper/Numeral-js/issues/596
       *
       * isAccuratePrice is always true here
       */
      throw "Too small number to format"
    }

    return options.noUnit ? formattedValue.replace(/^\$/, "") : formattedValue
  } catch {
    // Silence is golden
    // console.info(`Price fell back to zero due to "${error}" when input was "${value}"`)
    return `${options.noUnit ? "" : "$"}0.00`
  }
}

export const canUseNumeral = (value?: string | number) => {
  // Check if we can use numeraljs for formatting values because numeraljs returns NaN
  // if value is below 0.000000. If value is below, return false and just show the value as it is e.g 1e-6

  // can return true if value is undefined: numeral(undefined).value() === 0
  if (!value) return true

  return Math.abs(Number(value)) >= 1e-6
}
